import { useEffect, useRef, useState, Fragment } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { FilterOption } from '@odo/shared-ui';
import { DataTableFilterDropdown } from '@odo/shared-ui/DataTable';
import { useDeleteCommunicationEntry } from '../../../../hooks/useCommunications';
import type {
    CommunicationChannel,
    CommunicationEntry,
    CommunicationPatientRef
} from '../../../../services/communications';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { formatCreatedDate } from '../../../../utils/dates';
import { AddPatientNoteModal } from './AddPatientNoteModal';
import { CHANNELS } from './channels';
import { ComposerSpineRow } from './ComposerSpineRow';
import { DayLabel } from './DayLabel';
import { DeleteEntryConfirmModal } from './DeleteEntryConfirmModal';
import { DiscardChangesConfirmModal } from './DiscardChangesConfirmModal';
import { EntryComposer } from './EntryComposer';
import { EntryFoldCard } from './EntryFoldCard';

// Rendered `multiple` for the real checkbox square (single-select shows a
// near-invisible checkmark instead); onChange below collapses it back to
// one selection.
const CHANNEL_FILTER_OPTIONS: FilterOption[] = CHANNELS.map((c) => ({
    value: c.id,
    label: c.label
}));

type ComposerState =
    | { mode: 'closed' }
    | { mode: 'add'; channel: CommunicationChannel }
    | { mode: 'edit'; entryId: string };

interface CommunicationTimelineProps {
    communicationId: string;
    communicationTitle: string;
    practiceId: string | null;
    /** Ascending by occurredAt (the wire order) - reversed for display. */
    entries: CommunicationEntry[];
    /** The thread's linked patients - seeds AddPatientNoteModal's picker. */
    linkedPatients: CommunicationPatientRef[];
}

// Timeline spine with day separators, newest-first; the entry being
// written/edited sits in the composer's own slot. Only one composer is ever
// open, guarded by the same discard-changes flow as the create page.
export function CommunicationTimeline({
    communicationId,
    communicationTitle,
    practiceId,
    entries,
    linkedPatients
}: CommunicationTimelineProps) {
    const { showToast } = useToast();
    const [composer, setComposer] = useState<ComposerState>({ mode: 'closed' });
    const [composerDirty, setComposerDirty] = useState(false);
    const [pendingComposer, setPendingComposer] = useState<ComposerState | null>(null);
    const [deletingEntry, setDeletingEntry] = useState<CommunicationEntry | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [addNoteEntry, setAddNoteEntry] = useState<CommunicationEntry | null>(null);
    const deleteEntry = useDeleteCommunicationEntry();
    const composerSlotRef = useRef<HTMLDivElement>(null);
    // The ONLY scrollable region on the detail page now - the identity card,
    // this component's own toolbar row, and the rail all stay fixed (see
    // CommunicationDetailPage.tsx).
    const scrollRef = useRef<HTMLDivElement>(null);

    // Entries the viewer expanded past their long-body default clamp - a
    // per-card toggle only (see EntryFoldCard's useClampOverflow). Nothing
    // here tracks whether a whole CARD is folded - there's no such state.
    const [expandedLongIds, setExpandedLongIds] = useState<Set<string>>(() => new Set());
    const [channelFilter, setChannelFilter] = useState<CommunicationChannel | null>(null);

    // Adding scrolls to the composer at the top and focuses it;
    // `preventScroll: true` stops the focus call from fighting that scroll.
    // Edit-in-place skips this - the card is already in view.
    useEffect(() => {
        if (composer.mode !== 'add') return;
        scrollRef.current?.scrollTo({ top: 0 });
        const field = composerSlotRef.current?.querySelector<HTMLElement>(
            'input, [contenteditable="true"]'
        );
        field?.focus({ preventScroll: true });
    }, [composer]);

    const toggleLongExpand = (entryId: string) => {
        setExpandedLongIds((prev) => {
            const next = new Set(prev);
            if (next.has(entryId)) next.delete(entryId);
            else next.add(entryId);
            return next;
        });
    };

    // Applies a composer target directly, no confirmation - used once a
    // switch has already been cleared (or there was nothing to lose).
    const applyComposer = (next: ComposerState) => {
        setComposer(next);
        setComposerDirty(false);
    };

    // Every request to change what the composer is doing routes through
    // here, so a dirty in-progress entry (new or edited) is never silently
    // discarded by picking a different channel or clicking Edit elsewhere.
    const requestComposer = (next: ComposerState) => {
        if (composer.mode !== 'closed' && composerDirty) {
            setPendingComposer(next);
            return;
        }
        applyComposer(next);
    };

    // Always opens on the default channel ('call') - the composer's own
    // ChannelSegmentedControl is the channel picker, so there's no separate
    // choice to make before it opens.
    const handleAddEntryClick = () => requestComposer({ mode: 'add', channel: 'call' });
    const handleEditEntry = (entryId: string) => requestComposer({ mode: 'edit', entryId });
    const handleComposerCancel = () => requestComposer({ mode: 'closed' });
    // A completed save is never "discarded" - close directly, no prompt.
    const handleComposerDone = () => applyComposer({ mode: 'closed' });

    const confirmDiscard = () => {
        if (pendingComposer) applyComposer(pendingComposer);
        setPendingComposer(null);
    };
    const keepEditing = () => setPendingComposer(null);

    const handleConfirmDelete = () => {
        if (!deletingEntry) return;
        setDeleteError(null);
        deleteEntry.mutate(
            { id: communicationId, entryId: deletingEntry.id },
            {
                onSuccess: () => {
                    showToast('Interaction deleted', 'success');
                    setDeletingEntry(null);
                },
                onError: (err) => {
                    // The server rejects deleting a thread's last remaining
                    // entry (400) - surfaced inline in the modal rather than
                    // as a toast, so it reads as a reason, not a transient failure.
                    setDeleteError(apiErrorMessage(err, 'Failed to delete interaction'));
                }
            }
        );
    };

    // Newest-first, matching where the composer will land once saved.
    const descendingEntries = [...entries].reverse().filter((entry) => {
        if (!channelFilter || entry.channel === channelFilter) return true;
        // Never let the channel filter hide the entry actively being edited -
        // switching filters mid-edit shouldn't make the open composer vanish.
        return composer.mode === 'edit' && composer.entryId === entry.id;
    });

    let lastDay: string | null = null;

    return (
        <div className="flex-1 min-h-0 flex flex-col gap-4">
            <div className="flex items-center gap-3 shrink-0">
                <h2 className="text-[15px] font-bold text-gray-800">Interactions</h2>
                <span className="text-[13px] text-gray-400">{entries.length}</span>
                <div className="flex items-center gap-1.5 ml-auto">
                    <DataTableFilterDropdown
                        label="Channel"
                        multiple
                        options={CHANNEL_FILTER_OPTIONS}
                        value={channelFilter ? [channelFilter] : []}
                        onChange={(values) => {
                            // Multi-select UI, single-select result: keep
                            // whichever entry isn't the current selection.
                            const next = values.find((value) => value !== channelFilter) ?? null;
                            setChannelFilter(next as CommunicationChannel | null);
                        }}
                    />
                    {composer.mode !== 'add' && (
                        <button
                            type="button"
                            onClick={handleAddEntryClick}
                            className="flex items-center gap-1.5 bg-black text-white font-semibold px-3 h-8 rounded-lg text-[13px] hover:bg-gray-900 transition-colors shrink-0"
                        >
                            <PlusIcon className="h-3.5 w-3.5" />
                            Interaction
                        </button>
                    )}
                </div>
            </div>

            {/* The only scrollable region here - composer slot, day labels
                and cards all scroll together as one block. FloatingMenu's
                reposition listener is a capture-phase `window` listener, so
                it already catches scroll events fired on this (or any)
                nested scroll container without any change on its end -
                scroll events don't bubble, but they do fire on ancestors
                (window included) during the capture phase. */}
            <div
                ref={scrollRef}
                className="flex-1 min-h-0 overflow-y-auto scrollbar-hide flex flex-col"
            >
                {composer.mode === 'add' && (
                    <ComposerSpineRow mode="add" showConnector={false}>
                        <div ref={composerSlotRef}>
                            <EntryComposer
                                communicationId={communicationId}
                                practiceId={practiceId}
                                initialChannel={composer.channel}
                                onDone={handleComposerDone}
                                onCancel={handleComposerCancel}
                                onDirtyChange={setComposerDirty}
                            />
                        </div>
                    </ComposerSpineRow>
                )}

                <div className="flex flex-col">
                    {descendingEntries.length === 0 ? (
                        <p className="py-8 text-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-xl">
                            {entries.length === 0
                                ? 'No interactions yet - add the first one above.'
                                : 'No interactions on this channel.'}
                        </p>
                    ) : (
                        descendingEntries.map((entry, index) => {
                            const day = formatCreatedDate(entry.occurredAt);
                            const showDay = day !== lastDay;
                            lastDay = day;
                            const isLast = index === descendingEntries.length - 1;
                            const isEditingThis =
                                composer.mode === 'edit' && composer.entryId === entry.id;

                            return (
                                <Fragment key={entry.id}>
                                    {showDay && <DayLabel day={day} first={index === 0} />}
                                    {isEditingThis ? (
                                        <ComposerSpineRow mode="edit" showConnector={!isLast}>
                                            <EntryComposer
                                                communicationId={communicationId}
                                                practiceId={practiceId}
                                                entry={entry}
                                                onDone={handleComposerDone}
                                                onCancel={handleComposerCancel}
                                                onDirtyChange={setComposerDirty}
                                            />
                                        </ComposerSpineRow>
                                    ) : (
                                        <EntryFoldCard
                                            entry={entry}
                                            practiceId={practiceId}
                                            last={isLast}
                                            longExpanded={expandedLongIds.has(entry.id)}
                                            onToggleLongExpand={() => toggleLongExpand(entry.id)}
                                            onEdit={() => handleEditEntry(entry.id)}
                                            onAddPatientNote={() => setAddNoteEntry(entry)}
                                            onDelete={() => setDeletingEntry(entry)}
                                            linkedPatients={linkedPatients}
                                        />
                                    )}
                                </Fragment>
                            );
                        })
                    )}
                </div>
            </div>

            {pendingComposer && (
                <DiscardChangesConfirmModal
                    description="This interaction has unsaved changes. If you continue now they will be lost."
                    onKeepEditing={keepEditing}
                    onDiscard={confirmDiscard}
                />
            )}

            {deletingEntry && (
                <DeleteEntryConfirmModal
                    entry={deletingEntry}
                    isDeleting={deleteEntry.isPending}
                    errorMessage={deleteError}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => {
                        setDeletingEntry(null);
                        setDeleteError(null);
                    }}
                />
            )}

            {addNoteEntry && (
                <AddPatientNoteModal
                    entry={addNoteEntry}
                    communicationId={communicationId}
                    communicationTitle={communicationTitle}
                    practiceId={practiceId}
                    linkedPatients={linkedPatients}
                    onClose={() => setAddNoteEntry(null)}
                />
            )}
        </div>
    );
}
